import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import EarnixLogo, { EarnixWordmark } from "../components/EarnixLogo";
import useBinancePrices from "../hooks/useBinancePrices";
import API from "../config.js";
import HeroBackdrop from "../components/HeroBackdrop";


/* ══ 3D Flip Card (Plan Cards) ═══════════════════════════ */
function FlipCard({ front, back, style }) {
  const [flipped, setFlipped] = useState(false);
  return (
    <div
      style={{ perspective:"1000px", ...style }}
      onMouseEnter={() => setFlipped(true)}
      onMouseLeave={() => setFlipped(false)}
    >
      <div style={{
        position:"relative", width:"100%", height:"100%",
        transformStyle:"preserve-3d",
        transform: flipped ? "rotateY(180deg)" : "rotateY(0deg)",
        transition:"transform 0.6s cubic-bezier(0.4,0.2,0.2,1)",
      }}>
        {/* Front */}
        <div style={{
          position:"absolute", inset:0,
          backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden",
        }}>
          {front}
        </div>
        {/* Back */}
        <div style={{
          position:"absolute", inset:0,
          backfaceVisibility:"hidden", WebkitBackfaceVisibility:"hidden",
          transform:"rotateY(180deg)",
        }}>
          {back}
        </div>
      </div>
    </div>
  );
}

/* ══ 3D Text Heading ══════════════════════════════════════ */
function Text3D({ children, color = "#FFB800", size = "clamp(22px,4vw,36px)" }) {
  return (
    <h2 style={{
      fontFamily:"'Figtree',sans-serif",
      fontWeight:700, fontSize:size,
      color:color,
      letterSpacing:2, marginBottom:12,
      lineHeight:1.3,
      padding:"4px 0",
      textShadow:`
        0 1px 0 rgba(255,255,255,0.15),
        0 2px 0 rgba(0,0,0,0.4),
        0 3px 0 rgba(0,0,0,0.3),
        0 4px 0 rgba(0,0,0,0.2),
        0 5px 0 rgba(0,0,0,0.1),
        0 8px 20px rgba(0,0,0,0.5),
        0 0 30px ${color}66
      `,
      WebkitBackgroundClip:"text",
      background:`linear-gradient(135deg, #f0e0a0, ${color}, #C8860A)`,
      WebkitTextFillColor:"transparent",
      filter:`drop-shadow(0 4px 8px ${color}44)`,
    }}>
      {children}
    </h2>
  );
}

/* ══ 3D Tilt Card ═════════════════════════════════════════ */
function TiltCard({ children, style, className }) {
  const [tilt, setTilt] = useState({ x: 0, y: 0, glow: false });
  const onMove = e => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width  - 0.5;
    const y = (e.clientY - r.top)  / r.height - 0.5;
    setTilt({ x: y * 10, y: -x * 10, glow: true });
  };
  const onLeave = () => setTilt({ x: 0, y: 0, glow: false });
  return (
    <div style={{
      ...style,
      transform: `perspective(900px) rotateX(${tilt.x}deg) rotateY(${tilt.y}deg) scale(${tilt.glow ? 1.02 : 1})`,
      transition: "transform 0.15s ease",
      willChange: "transform",
    }}
      onMouseMove={onMove}
      onMouseLeave={onLeave}
    >
      {children}
    </div>
  );
}

/* ══ Scroll Reveal Section ════════════════════════════════ */
function Reveal({ children, style, delay = 0 }) {
  const ref = useRef(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } },
      { threshold: 0.08 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} style={{
      ...style,
      opacity:   vis ? 1 : 0,
      transform: vis ? "translateY(0)" : "translateY(36px)",
      transition: `opacity 0.7s ease ${delay}ms, transform 0.7s ease ${delay}ms`,
    }}>
      {children}
    </div>
  );
}

/* ══ Number Counter ═══════════════════════════════════════ */
function useCounter(target, started, duration = 1800) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!started) return;
    const t0 = Date.now();
    const id = setInterval(() => {
      const p = Math.min((Date.now() - t0) / duration, 1);
      setVal(Math.floor(p * target));
      if (p >= 1) clearInterval(id);
    }, 16);
    return () => clearInterval(id);
  }, [target, started, duration]);
  return val;
}

/* ── Palette ─────────────────────────────────────────────── */
const G    = "#FFB800";
const G2   = "#FFD700";
const GD   = "#C8860A";
const C    = "#00C2FF";
const C2   = "#00E5FF";
const GRAD = `linear-gradient(135deg, ${G2}, ${G}, ${GD})`;

/* ── Country names ──────────────────────────────────────── */
const COUNTRY_NAMES = {
  "India":["Arjun S.","Priya R.","Rahul M.","Meera J.","Sanjay P."],
  "USA":["James K.","Sarah L.","Michael T.","Emily R.","John T."],
  "UAE":["Mohammed A.","Fatima N.","Omar F.","Aisha B.","Hassan A."],
  "UK":["Oliver B.","Emma W.","Harry C.","Sophie T.","George M."],
  "Singapore":["Wei C.","Jing L.","Ming H.","Kai T.","Hui Y."],
  "Canada":["Liam O.","Olivia B.","Noah T.","Ava M.","Ethan R."],
  "Australia":["Jack H.","Mia W.","Lucas T.","Chloe B.","William R."],
  "Germany":["Hans M.","Elena V.","Klaus B.","Anna S.","Lena K."],
  "Japan":["Yuki T.","Sakura H.","Kenji M.","Aiko N.","Hiroshi T."],
  "Malaysia":["Ahmad R.","Nurul H.","Aziz K.","Siti M.","Farah B."],
  "Nigeria":["David O.","Amina K.","Chidi E.","Ngozi A.","Emeka O."],
  "Brazil":["Lucas R.","Ana L.","Carlos D.","Julia F.","Pedro S."],
  "South Korea":["Ji-ho K.","Min-jun L.","Soo-yeon P.","Hyun-ji C.","Tae-yang K."],
  "Turkey":["Mehmet Y.","Ayse D.","Ali K.","Fatma S.","Mustafa B."],
  "Mexico":["Carlos M.","Sofía G.","Luis H.","Valentina R.","Diego F."],
};
const FLAGS = {
  India:"🇮🇳",USA:"🇺🇸",UAE:"🇦🇪",UK:"🇬🇧",Singapore:"🇸🇬",Canada:"🇨🇦",
  Australia:"🇦🇺",Germany:"🇩🇪",Japan:"🇯🇵",Malaysia:"🇲🇾",Nigeria:"🇳🇬",
  Brazil:"🇧🇷","South Korea":"🇰🇷",Turkey:"🇹🇷",Mexico:"🇲🇽",
};
const COUNTRIES = Object.keys(COUNTRY_NAMES).map(n => ({ name:n, flag:FLAGS[n] }));
const ACTIONS   = ["just joined","deposited","earned referral bonus","just invested"];


/* ══════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════ */
export default function Home() {
  const binancePrices = useBinancePrices();
  // Map to format Home page expects: { symbol, price, pct, up }
  const prices = binancePrices.map(p => ({ ...p, symbol: p.pair }));
  const [userCount, setUserCount] = useState(18247);
  const [volume,    setVolume]    = useState(4812650);
  const [realBase,  setRealBase]  = useState({ members: 0, paidOut: 0 });
  const [joinFeed,  setJoinFeed]  = useState([]);
  const [contactForm, setContactForm] = useState({ name:"", email:"", message:"" });
  const [statsVis,  setStatsVis]  = useState(false);
  const statsRef = useRef(null);

  /* Stats counter trigger */
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setStatsVis(true); obs.disconnect(); } },
      { threshold: 0.3 }
    );
    if (statsRef.current) obs.observe(statsRef.current);
    return () => obs.disconnect();
  }, []);

  const cntMembers = useCounter(18247, statsVis, 2000);
  const cntVolume  = useCounter(4812, statsVis, 2200);
  const cntCountry = useCounter(100, statsVis, 1600);
  const [contactSent, setContactSent] = useState(false);


  /* Fetch real stats from DB, then keep fake increments on top */
  useEffect(() => {
    fetch(`${API}/api/stats`)
      .then(r => r.json())
      .then(d => {
        const realMembers = d.members  || 0;
        const realPaid    = d.paidOut  || 0;
        setRealBase({ members: realMembers, paidOut: realPaid });
        // Set initial display: real + fake base (so it always looks big)
        setUserCount(n  => Math.max(n,  realMembers + 18000));
        setVolume(n     => Math.max(n,  realPaid    + 4800000));
      })
      .catch(() => {});
  }, []);

  /* User counter — real base + fake increments, always growing */
  useEffect(() => {
    const id = setInterval(() => {
      setUserCount(n => n + Math.floor(Math.random() * 3 + 1));
      setVolume(n    => n + Math.floor(Math.random() * 500 + 50));
    }, 3000);
    return ()=>clearInterval(id);
  }, []);

  /* Join feed */
  useEffect(() => {
    const add = () => {
      const country = COUNTRIES[Math.floor(Math.random()*COUNTRIES.length)];
      const names   = COUNTRY_NAMES[country.name];
      const name    = names[Math.floor(Math.random()*names.length)];
      const action  = ACTIONS[Math.floor(Math.random()*ACTIONS.length)];
      const amt     = (Math.random()*2000+100).toFixed(2);
      setJoinFeed(prev=>[{id:Date.now(),country,name,action,amt},...prev].slice(0,6));
    };
    add();
    const id = setInterval(add, 4000);
    return ()=>clearInterval(id);
  }, []);

  const handleContact = e => {
    e.preventDefault();
    setContactSent(true);
    setContactForm({ name:"", email:"", message:"" });
  };

  return (
    <div style={h.page}>
      <style>{`
        @keyframes smoothTicker { 0% { transform:translate3d(0,0,0) } 100% { transform:translate3d(-50%,0,0) } }
        @keyframes floatUp { 0%,100% { transform:translateY(0px) } 50% { transform:translateY(-12px) } }
        @keyframes logoPulse { 0%,100% { filter:drop-shadow(0 0 18px rgba(255,184,0,0.5)) } 50% { filter:drop-shadow(0 0 36px rgba(255,184,0,0.9)) } }
        @keyframes pulse-glow { 0%,100% { opacity:1;transform:scale(1) } 50% { opacity:0.6;transform:scale(1.3) } }
        @keyframes fadeSlideIn { from { opacity:0;transform:translateY(-10px) } to { opacity:1;transform:translateY(0) } }
        .hero-backdrop {
          position: fixed; inset: 0; width: 100%;
          height: 100%; height: 100dvh;
          pointer-events: none; z-index: 0;
        }
        @media (max-width: 800px) {
          .prelaunch-grid { grid-template-columns: 1fr !important; }
          .prelaunch-title { font-size: 26px !important; line-height: 1.25 !important; }
          .prelaunch-card {
            overflow: visible !important;
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
          }
          .hero-section { padding: 28px 16px 48px !important; }
          .hero-section, .hero-section * {
            backdrop-filter: none !important;
            -webkit-backdrop-filter: none !important;
          }
          .logo-anim { animation: none !important; }
        }
      `}</style>
      <Navbar />

      {/* ── LIVE TICKER ──────────────────────────────── */}
      <TickerBar />

      {/* ══ HERO ════════════════════════════════════════ */}
      <section className="hero-section" style={h.hero}>
        <div className="hero-backdrop">
          <HeroBackdrop />
        </div>
        <div style={{
          position:"absolute", bottom:"0%", left:"0", right:"0",
          height:"200px", pointerEvents:"none",
          background:"linear-gradient(to top, #060D1A, transparent)",
          zIndex:2,
        }}/>

        <div style={h.heroContent}>
          <div className="prelaunch-card" style={h.prelaunchCard}>
            <div style={h.secBadge}>PRELAUNCH</div>
            <h2 className="prelaunch-title" style={h.prelaunchTitle}>Prelaunch is open</h2>
            <p style={h.prelaunchLead}>
              Earnix Global launches on 19 December 2026. You can join now. Deposit, withdrawal, and earnings are already working in this prelaunch period.
            </p>
            <div className="prelaunch-grid" style={h.prelaunchGrid}>
              {[
                "Create your account and join before the public launch",
                "Deposit USDT and start earning now",
                "Daily earnings run during prelaunch",
                "Your account, balance, and earnings carry over on launch day",
                "Withdrawals are enabled during prelaunch. You can withdraw your earnings any time",
                "On 19 December 2026 the same account continues. You do not register again",
              ].map(line => (
                <div key={line} style={h.prelaunchItem}>{line}</div>
              ))}
            </div>
            <Link to={localStorage.getItem("token") ? "/dashboard" : "/login?join=1"} style={h.btnPrimary}>Join now</Link>
          </div>

          {/* Logo — no glow, logo has dark background built in */}
          <div className="logo-anim" style={h.logoAnim}>
            <EarnixLogo size={120} />
          </div>

          {/* Taglines */}
          <div style={h.tagline}>Intelligent Wealth. Infinite Network.</div>
          <div style={h.subTagline}>AI-Powered Crypto MLM Platform</div>

          {/* Pills */}
          <div style={h.pills}>
            {["BLOCKCHAIN","AI · MLM","CRYPTO","USDT"].map(b=>(
              <span key={b} style={h.pill}>{b}</span>
            ))}
          </div>

          {/* Live badge */}
          <div style={h.liveBadge}>
            <span style={h.liveDot}/>
            Live Trading Active &nbsp;·&nbsp; {userCount.toLocaleString()}+ Members Worldwide
          </div>

          {/* Main headline — 3D text effect */}
          <h1 style={h.headline}>
            <span style={{
              fontWeight: 900,
              background: "linear-gradient(135deg, #FFE875 0%, #FFB800 50%, #C8860A 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: "transparent",
            }}>EARNIX</span>
            <span style={{
              fontWeight: 900,
              background: "linear-gradient(135deg, #FFFFFF 0%, #D8E2F5 50%, #A8B8D8 100%)",
              WebkitBackgroundClip: "text",
              backgroundClip: "text",
              WebkitTextFillColor: "transparent",
              color: "transparent",
            }}> GLOBAL</span>
          </h1>
          <p style={h.heroPara}>
            The world's most intelligent crypto earnings platform.<br/>
            Powered by AI · Secured by Blockchain · Rewarded by Community.
          </p>

          {/* CTA Buttons */}
          <div style={h.ctaBtns}>
            <Link to={localStorage.getItem("token") ? "/dashboard" : "/login"} style={h.btnPrimary}>Start Earning Now →</Link>
            <a href="#how-it-works" style={h.btnGhost}>How It Works</a>
          </div>

          {/* Stat pills */}
          <div ref={statsRef} style={h.statRow}>
            {[
              {label:"Active Members", val:cntMembers.toLocaleString()+"+", color:G},
              {label:"USDT Volume",    val:"$"+cntVolume+"K+",              color:G2},
              {label:"Countries",      val:cntCountry+"+",                  color:C},
              {label:"Daily Return",   val:"1.5%",                          color:"#00e87a"},
            ].map((st,i)=>(
              <div key={i} style={{...h.statCard, borderColor:st.color+"30"}}>
                <span style={{...h.statVal,color:st.color}}>{st.val}</span>
                <span style={h.statLabel}>{st.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <div style={{ position:"relative", zIndex:1, background:"linear-gradient(160deg, #071428 0%, #050E1E 40%, #081628 70%, #040C1A 100%)" }}>
      {/* ══ LIVE MARKETS ════════════════════════════════ */}
      <section style={h.section}>
        <div style={h.container}>
          <Reveal><SecHead badge="LIVE" title="Real-Time AI Markets" /></Reveal>
          <div style={h.priceGrid}>
            {prices.map((c,i)=>(
              <Reveal key={i} delay={i*80}>
                <TiltCard style={{height:"100%"}}><PriceCard data={c}/></TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ LIVE ACTIVITY ════════════════════════════════ */}
      <section style={{...h.section, background:"rgba(0,194,255,0.02)", borderTop:`1px solid rgba(0,194,255,0.06)`, borderBottom:`1px solid rgba(0,194,255,0.06)`}}>
        <div style={h.container}>
          <SecHead badge="LIVE" title="People Joining Right Now" sub="Real members from across the globe joining Earnix Global" />
          <div style={h.actGrid}>
            <div style={h.feedCard}>
              <div style={h.feedHd}>
                <span style={h.liveTag}><span style={h.liveDot2}/>LIVE ACTIVITY</span>
              </div>
              {joinFeed.map((j,i)=>(
                <div key={j.id} style={{...h.feedRow, opacity:1-i*0.1,
                  animation:i===0?"fadeSlideIn 0.5s ease":"none"}}>
                  <span style={{fontSize:22}}>{j.country.flag}</span>
                  <div style={h.feedText}>
                    <b style={{color:"#e0e8ff"}}>{j.name}</b>
                    <span style={{color:"rgba(160,180,220,0.6)"}}> from {j.country.name} </span>
                    <span style={{color:G}}>{j.action}</span>
                    {(j.action.includes("deposit")||j.action.includes("invest"))&&(
                      <b style={{color:G2}}> · {j.amt} USDT</b>
                    )}
                  </div>
                  <span style={h.feedTime}>just now</span>
                </div>
              ))}
            </div>
            <div style={h.bigStats}>
              {[
                {icon:"👥",val:userCount.toLocaleString()+"+",label:"Total Members",color:G},
                {icon:"💸",val:"$"+(volume/1000).toFixed(0)+"K+",label:"USDT Paid Out",color:G2},
                {icon:"🌍",val:"100+",label:"Countries",color:C},
                {icon:"⚡",val:"99.9%",label:"Uptime",color:"#00e87a"},
              ].map((s,i)=>(
                <div key={i} style={{...h.bigStat, borderColor:s.color+"20"}}>
                  <div style={{...h.bigIcon, color:s.color, background:s.color+"12"}}>{s.icon}</div>
                  <div style={{...h.bigVal, color:s.color}}>{s.val}</div>
                  <div style={h.bigLabel}>{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ══ WHO WE ARE ══════════════════════════════════ */}
      <section id="about" style={h.section}>
        <div style={h.container}>
          <Reveal><SecHead badge="ABOUT US" title="Who We Are" sub="The team behind Earnix Global" /></Reveal>
          <div style={h.whoGrid}>
            <Reveal delay={0}><TiltCard style={h.glassCard}>
              <div style={h.whoIcon}>🏛️</div>
              <h3 style={h.whoTitle}>Our Story</h3>
              <p style={h.whoPara}>
                Earnix Global was founded in 2023 by a team of blockchain engineers, AI researchers, and fintech veterans with a shared vision: democratize wealth creation through decentralized technology. What began as a research project has grown into a global platform trusted by over 18,000 members across 100+ countries.
              </p>
            </TiltCard></Reveal>
            <Reveal delay={120}><TiltCard style={h.glassCard}>
              <div style={h.whoIcon}>🧠</div>
              <h3 style={h.whoTitle}>Our Team</h3>
              <p style={h.whoPara}>
                We are a diverse team of 50+ professionals spanning AI development, blockchain security, quantitative finance, and community building. Our engineers have backgrounds from MIT, IIT, and leading tech companies. We operate 24/7 across offices in Singapore, Dubai, and Malaysia.
              </p>
            </TiltCard></Reveal>
            <Reveal delay={240}><TiltCard style={h.glassCard}>
              <div style={h.whoIcon}>🛡️</div>
              <h3 style={h.whoTitle}>Our Commitment</h3>
              <p style={h.whoPara}>
                Transparency is our core principle. Every transaction is logged on-chain, every payout is verifiable, and every member has complete visibility into their earnings. We are committed to building a platform where technology and trust go hand in hand.
              </p>
            </TiltCard></Reveal>
          </div>
        </div>
      </section>

      {/* ══ HOW WE WORK ═════════════════════════════════ */}
      <section id="how-it-works" style={{...h.section, background:"rgba(255,184,0,0.018)", borderTop:`1px solid rgba(255,184,0,0.07)`, borderBottom:`1px solid rgba(255,184,0,0.07)`}}>
        <div style={h.container}>
          <Reveal><SecHead badge="HOW IT WORKS" title="How We Work" sub="Three powerful engines driving your earnings" /></Reveal>
          <div style={h.howGrid}>
            {[
              {
                num:"01", icon:"🤖", title:"AI Trading Engine",
                desc:"Our proprietary AI model analyses 200+ technical indicators, sentiment data, and on-chain signals across 50+ trading pairs simultaneously. The engine executes micro-strategies 24/7, optimising for maximum risk-adjusted returns. Members benefit from institutional-grade algo trading without needing any expertise.",
                color:G,
              },
              {
                num:"02", icon:"⛓️", title:"Referral Reward System",
                desc:"Every time someone in your network makes a deposit, you earn instantly. Direct referrals earn you 10% immediately. Beyond that, our 10-level MLM structure distributes income across your entire downline — so your earnings grow exponentially as your network expands.",
                color:C,
              },
              {
                num:"03", icon:"💎", title:"USDT Smart Payouts",
                desc:"All earnings are denominated in USDT — the world's most stable crypto asset. Withdraw anytime you choose, with a 5% processing fee. Funds are processed by our treasury within 24 hours and sent directly to your wallet, with zero hidden charges.",
                color:"#00e87a",
              },
            ].map((item,i)=>(
              <Reveal key={i} delay={i*120}>
              <TiltCard style={{...h.howCard, borderColor:item.color+"22"}}>
                <div style={h.howNum}>{item.num}</div>
                <div style={{fontSize:40,marginBottom:16}}>{item.icon}</div>
                <h3 style={{...h.howTitle, color:item.color}}>{item.title}</h3>
                <p style={h.howDesc}>{item.desc}</p>
                <div style={{...h.howAccent, background:item.color+"10", borderColor:item.color+"30"}} />
              </TiltCard>
              </Reveal>
            ))}
          </div>

          {/* Steps timeline */}
          <div style={h.stepsRow}>
            {[
              {n:"01",t:"Create Account",d:"Register free in 2 minutes. Use a referral code to unlock instant team bonuses."},
              {n:"02",t:"Deposit USDT",d:"Fund your wallet with USDT on any network. No minimum deposit required."},
              {n:"03",t:"AI Works For You",d:"Our AI engine trades markets 24/7, generating returns directly to your balance."},
              {n:"04",t:"Earn & Withdraw",d:"Withdraw USDT to your wallet anytime, or reinvest to compound your returns."},
            ].map((s,i)=>(
              <div key={i} style={h.stepCard}>
                <div style={h.stepNumCircle}>{s.n}</div>
                <div style={h.stepTitle}>{s.t}</div>
                <div style={h.stepDesc}>{s.d}</div>
                {i<3&&<div style={h.stepArrow}>›</div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ OUR VISION ══════════════════════════════════ */}
      <section style={h.section}>
        <div style={h.container}>
          <Reveal><SecHead badge="VISION" title="Our Vision & Mission" sub="Where we are going and why it matters" /></Reveal>
          <div style={h.visionGrid}>
            <Reveal delay={0}><div style={{...h.visionCard, borderColor:G+"30", background:"rgba(255,184,0,0.03)"}}>
              <div style={h.visionIcon}>🚀</div>
              <h3 style={{...h.visionTitle, color:G}}>Our Vision</h3>
              <p style={h.visionPara}>
                To become the world's most trusted AI-powered earnings ecosystem — where every individual, regardless of financial background, can access institutional-level crypto wealth building tools. We envision a world where financial freedom is not a privilege but a right enabled by technology.
              </p>
            </div></Reveal>
            <Reveal delay={120}><div style={{...h.visionCard, borderColor:C+"30", background:"rgba(0,194,255,0.03)"}}>
              <div style={h.visionIcon}>🎯</div>
              <h3 style={{...h.visionTitle, color:C}}>Our Mission</h3>
              <p style={h.visionPara}>
                To deploy cutting-edge artificial intelligence in service of everyday people — automating complex market analysis, eliminating emotional trading bias, and distributing profits fairly across our global community through transparent, blockchain-verified transactions.
              </p>
            </div></Reveal>
            <Reveal delay={240}><div style={{...h.visionCard, borderColor:"#00e87a30", background:"rgba(0,232,122,0.03)"}}>
              <div style={h.visionIcon}>💡</div>
              <h3 style={{...h.visionTitle, color:"#00e87a"}}>Our Values</h3>
              <p style={h.visionPara}>
                <b style={{color:"#00e87a"}}>Transparency</b> — every USDT tracked on-chain.<br/>
                <b style={{color:G}}>Innovation</b> — AI-first approach to every problem.<br/>
                <b style={{color:C}}>Community</b> — your network is your net worth.<br/>
                <b style={{color:"#ff9900"}}>Security</b> — military-grade protection of your assets.
              </p>
            </div></Reveal>
          </div>
        </div>
      </section>

      {/* ══ FEATURES ════════════════════════════════════ */}
      <section style={{...h.section, background:"rgba(0,0,0,0.3)"}}>
        <div style={h.container}>
          <Reveal><SecHead badge="PLATFORM" title="Why Earnix Global?" sub="Everything built for one purpose — your financial growth" /></Reveal>
          <div style={h.featGrid}>
            {[
              {icon:"🤖",t:"AI Trading Engine",d:"200+ signals analysed per second. Fully automated, zero expertise required.",color:G},
              {icon:"💰",t:"10% Instant Referral",d:"Earn 10% USDT instantly the moment your referrals deposit.",color:G2},
              {icon:"⛓️",t:"10-Level MLM Income",d:"Passive income from 10 levels deep — your network works for you.",color:C},
              {icon:"🔒",t:"Military-Grade Security",d:"AES-256 encryption, 2FA support, and on-chain transaction logs.",color:"#00e87a"},
              {icon:"⚡",t:"Instant USDT Deposits",d:"Deposits credited in seconds. No waiting, no delays.",color:"#ff9900"},
              {icon:"🌍",t:"100+ Countries",d:"Fully global platform. Join from anywhere, earn in USDT.",color:"#a855f7"},
              {icon:"📊",t:"Real-Time Dashboard",d:"Track every earning, referral, and transaction in real time.",color:G},
              {icon:"🎯",t:"Zero Hidden Fees",d:"Just 5% on withdrawals. Everything else is yours to keep.",color:C},
              {icon:"📱",t:"24/7 Support",d:"Message us on Telegram @earnix_global. Our team is always online.",color:"#00e87a"},
            ].map((f,i)=>(
              <Reveal key={i} delay={i*60}>
                <TiltCard style={{...h.featCard, borderColor:f.color+"18", height:"100%"}}>
                  <div style={{...h.featIcon, color:f.color, background:f.color+"10"}}>{f.icon}</div>
                  <div style={{...h.featTitle, color:f.color}}>{f.t}</div>
                  <div style={h.featDesc}>{f.d}</div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ══ DAILY INCOME PLANS ══════════════════════════ */}
      <section style={{...h.section, background:"rgba(255,184,0,0.018)", borderTop:`1px solid rgba(255,184,0,0.07)`, borderBottom:`1px solid rgba(255,184,0,0.07)`}}>
        <div style={h.container}>
          <Reveal><SecHead badge="INVEST" title="Daily Income Plans" sub="Invest USDT — earn daily interest automatically to your wallet" /></Reveal>
          <div style={h.dailyGrid}>
            {[
              { name:"Starter",  range:"50 – 499",     rate:"1.0%",  daily:"0.50 – 4.99",  color:"#00C2FF", icon:"🌱", eg:"100 USDT = 1 USDT/day",    monthlyEst:"~30 USDT",  locked:"7 days",  bonus:"No bonus" },
              { name:"Silver",   range:"500 – 1,499",  rate:"1.1%",  daily:"5.50 – 16.49", color:"#C0C8D8", icon:"⚡", eg:"1000 USDT = 11 USDT/day",  monthlyEst:"~330 USDT", locked:"14 days", bonus:"5% top-up bonus" },
              { name:"Gold",     range:"1,500 – 2,999",rate:"1.25%", daily:"18.75 – 37.49",color:G2,         icon:"🥇", eg:"2000 USDT = 25 USDT/day",  monthlyEst:"~750 USDT", locked:"21 days", bonus:"10% top-up bonus" },
              { name:"Platinum", range:"3,000 – 4,999",rate:"1.45%", daily:"43.50 – 72.49",color:"#B8A0FF",  icon:"💎", eg:"4000 USDT = 58 USDT/day",  monthlyEst:"~1740 USDT",locked:"30 days", bonus:"Priority support" },
              { name:"Elite",    range:"5,000+",        rate:"1.7%",  daily:"85+",           color:"#FF9900", icon:"👑", eg:"5000 USDT = 85 USDT/day",  monthlyEst:"~2550 USDT",locked:"30 days", bonus:"VIP + 15% bonus" },
            ].map((plan,i)=>(
              <Reveal key={i} delay={i*80}>
                <FlipCard
                  style={{ height:270 }}
                  front={
                    <div style={{
                      ...h.dailyCard,
                      borderColor: plan.color+"35",
                      background: i===4 ? "rgba(255,153,0,0.06)" : "rgba(8,18,40,0.85)",
                      height:"100%", cursor:"pointer",
                      boxShadow:`0 4px 24px ${plan.color}18, inset 0 0 40px rgba(0,0,0,0.2)`,
                    }}>
                      <div style={{...h.dailyTopLine, background: plan.color}} />
                      <div style={{fontSize:36, marginBottom:10}}>{plan.icon}</div>
                      <div style={{...h.dailyName, color: plan.color}}>{plan.name}</div>
                      <div style={{...h.dailyRate, color: plan.color}}>{plan.rate}<span style={{fontSize:14,fontWeight:400,fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif"}}>/day</span></div>
                      <div style={h.dailyRange}>{plan.range} USDT</div>
                      <div style={{...h.dailyEg, borderColor: plan.color+"25", color: plan.color+"CC"}}>{plan.eg}</div>
                      <div style={{marginTop:12, fontSize:10, color:"rgba(160,180,220,0.4)", fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif"}}>Hover for details →</div>
                    </div>
                  }
                  back={
                    <div style={{
                      ...h.dailyCard,
                      padding:"16px 18px",
                      borderColor: plan.color+"55",
                      background: `linear-gradient(135deg, rgba(8,18,40,0.97), ${plan.color}11)`,
                      height:"100%",
                      boxShadow:`0 8px 40px ${plan.color}33, inset 0 0 60px rgba(0,0,0,0.3)`,
                      display:"flex", flexDirection:"column", justifyContent:"center", gap:7,
                    }}>
                      <div style={{...h.dailyTopLine, background:`linear-gradient(90deg,${plan.color},transparent)`}} />
                      <div style={{fontSize:24}}>{plan.icon}</div>
                      <div style={{...h.dailyName, color: plan.color, fontSize:14, marginBottom:2}}>{plan.name} Plan</div>
                      {[
                        ["Daily Rate",   plan.rate],
                        ["Investment",   plan.range+" USDT"],
                        ["Monthly Est.", plan.monthlyEst],
                      ].map(([k,v])=>(
                        <div key={k} style={{
                          display:"flex", justifyContent:"space-between",
                          padding:"5px 10px",
                          background:"rgba(0,0,0,0.25)",
                          borderRadius:6,
                          border:`1px solid ${plan.color}18`,
                        }}>
                          <span style={{fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:11, color:"rgba(160,180,220,0.55)"}}>{k}</span>
                          <span style={{fontFamily:"'Figtree',sans-serif", fontSize:10, color:plan.color, fontWeight:700}}>{v}</span>
                        </div>
                      ))}
                      <Link to="/login" style={{
                        marginTop:6, display:"block", textAlign:"center",
                        fontFamily:"'Figtree',sans-serif",
                        fontWeight:700, fontSize:10,
                        color:"#060D1A",
                        background:`linear-gradient(135deg,${plan.color},${plan.color}aa)`,
                        border:"none", borderRadius:6,
                        padding:"9px 0", textDecoration:"none",
                        letterSpacing:1,
                        boxShadow:`0 4px 14px ${plan.color}44`,
                      }}>Invest Now →</Link>
                    </div>
                  }
                />
              </Reveal>
            ))}
          </div>
          <Reveal>
            <div style={h.dailyNote}>
              <span style={{color:G}}>✦</span> Interest credited daily at 12:00 AM UTC to your wallet &nbsp;·&nbsp;
              <span style={{color:G}}>✦</span> Withdraw capital anytime &nbsp;·&nbsp;
              <span style={{color:G}}>✦</span> Plan auto-upgrades when you top-up
            </div>
          </Reveal>
        </div>
      </section>

      {/* ══ MLM LEVELS ══════════════════════════════════ */}
      <section style={h.section}>
        <div style={h.container}>
          <SecHead badge="REFERRAL" title="10-Level MLM Income Structure" sub="Your network generates income 10 levels deep" />
          <div style={h.levelGrid}>
            {[
              {l:"L1",pct:"10%",desc:"Direct referral — 10% on every deposit",color:G2,big:true},
              {l:"L2",pct:"5%", desc:"Level 2 downline earnings",color:G},
              {l:"L3",pct:"3%", desc:"Level 3 downline earnings",color:G},
              {l:"L4",pct:"2%", desc:"Level 4 downline earnings",color:GD},
              {l:"L5",pct:"1%", desc:"Level 5 downline earnings",color:GD},
              {l:"L6",pct:"1%", desc:"Level 6 downline earnings",color:"rgba(200,134,10,0.7)"},
              {l:"L7",pct:"0.5%",desc:"Level 7 downline earnings",color:"rgba(200,134,10,0.6)"},
              {l:"L8",pct:"0.5%",desc:"Level 8 downline earnings",color:"rgba(200,134,10,0.5)"},
              {l:"L9",pct:"0.3%",desc:"Level 9 downline earnings",color:"rgba(200,134,10,0.4)"},
              {l:"L10",pct:"0.3%",desc:"Level 10 downline earnings",color:"rgba(200,134,10,0.35)"},
            ].map((lv,i)=>(
              <div key={i} style={{
                ...h.levelCard,
                borderColor:lv.color+"40",
                background:lv.big?"rgba(255,215,0,0.06)":"rgba(8,18,40,0.7)",
                transform:lv.big?"scale(1.04)":"none",
              }}>
                <div style={{...h.levelLabel, color:lv.color}}>{lv.l}</div>
                <div style={{...h.levelPct, color:lv.color}}>{lv.pct}</div>
                <div style={h.levelDesc}>{lv.desc}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ══ CONTACT US ══════════════════════════════════ */}
      <section id="contact" style={{...h.section, background:"rgba(0,194,255,0.02)", borderTop:`1px solid rgba(0,194,255,0.06)`, borderBottom:`1px solid rgba(0,194,255,0.06)`}}>
        <div style={h.container}>
          <SecHead badge="CONTACT" title="Get In Touch" sub="Our team is here 24/7 — reach out anytime" />
          <div style={h.contactGrid}>
            {/* Info */}
            <div style={h.contactInfo}>
              {[
                {icon:"📧", label:"Email Support",    val:"support@earnixglobal.com"},
                {icon:"💬", label:"Telegram",         val:"@earnix_global", href:"https://t.me/earnix_global"},
                {icon:"🐦", label:"Twitter / X",      val:"@EarnixGlobal"},
                {icon:"📍", label:"Headquarters",     val:"Singapore · Dubai · Malaysia"},
                {icon:"⏰", label:"Support Hours",    val:"24 / 7 / 365"},
              ].map((c,i)=>(
                <div key={i} style={h.contactItem}>
                  <span style={h.contactIcon}>{c.icon}</span>
                  <div>
                    <div style={h.contactLabel}>{c.label}</div>
                    {c.href
                      ? <a href={c.href} target="_blank" rel="noreferrer" style={{...h.contactVal, color:"#FFD700", textDecoration:"none"}}>{c.val}</a>
                      : <div style={h.contactVal}>{c.val}</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* Form */}
            <div style={h.contactCard}>
              <div style={h.cardTopLine}/>
              {contactSent ? (
                <div style={h.contactSuccess}>
                  <div style={{fontSize:48,marginBottom:16}}>✅</div>
                  <h3 style={{color:G2,fontFamily:"'Figtree',sans-serif",fontSize:18,letterSpacing:1,marginBottom:8}}>Message Sent!</h3>
                  <p style={{color:"rgba(160,180,220,0.6)",fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif"}}>
                    Our team will get back to you within 24 hours.
                  </p>
                  <button onClick={()=>setContactSent(false)} style={h.contactResetBtn}>Send Another</button>
                </div>
              ) : (
                <form onSubmit={handleContact} style={h.contactForm}>
                  <h3 style={h.contactFormTitle}>Send Us a Message</h3>
                  {[
                    {key:"name",  label:"Your Name",    type:"text",     placeholder:"John Doe"},
                    {key:"email", label:"Email Address", type:"email",    placeholder:"you@example.com"},
                  ].map(f=>(
                    <div key={f.key} style={h.fWrap}>
                      <label style={h.fLabel}>{f.label}</label>
                      <input type={f.type} required placeholder={f.placeholder}
                        value={contactForm[f.key]}
                        onChange={e=>setContactForm(p=>({...p,[f.key]:e.target.value}))}
                        style={h.fInput}/>
                    </div>
                  ))}
                  <div style={h.fWrap}>
                    <label style={h.fLabel}>Message</label>
                    <textarea required rows={4} placeholder="How can we help you?"
                      value={contactForm.message}
                      onChange={e=>setContactForm(p=>({...p,message:e.target.value}))}
                      style={{...h.fInput, resize:"vertical", minHeight:100}}/>
                  </div>
                  <button type="submit" style={h.contactSubmitBtn}>Send Message →</button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* ══ FINAL CTA ═══════════════════════════════════ */}
      <section style={h.ctaSection}>
        <div style={h.ctaGlow}/>
        <div style={{...h.container, textAlign:"center", position:"relative"}}>
          <EarnixLogo size={90} />
          <h2 style={h.ctaTitle}>Ready to Join the Future?</h2>
          <p style={h.ctaSub}>
            18,000+ members are already earning daily USDT with Earnix Global.<br/>
            Start your journey — it takes less than 2 minutes.
          </p>
          <div style={{display:"flex",gap:14,justifyContent:"center",flexWrap:"wrap"}}>
            <Link to={localStorage.getItem("token") ? "/dashboard" : "/login"} style={h.btnPrimary}>Create Free Account →</Link>
            <a href="#how-it-works" style={h.btnGhost}>Learn More</a>
          </div>
        </div>
      </section>

      {/* ══ FOOTER ══════════════════════════════════════ */}
      <footer style={h.footer}>
        <div style={h.footerInner}>
          <div style={h.footerTop}>
            <div style={h.footerBrand}>
              <EarnixLogo size={36} />
              <EarnixWordmark size={1} dark />
            </div>
            <div style={h.footerLinks}>
              {[
                {label:"About",href:"#about"},
                {label:"How It Works",href:"#how-it-works"},
                {label:"Contact Us",href:"/contact"},
                {label:"Terms & Conditions",href:"/terms"},
                {label:"Sign In",href:"/login"},
              ].map(l=>(
                <a key={l.label} href={l.href} style={h.footerLink}>{l.label}</a>
              ))}
            </div>
          </div>
          <div style={h.footerDivider}/>
          <div style={h.footerBottom}>
            <div style={h.footerCopy}>© {new Date().getFullYear()} Earnix Global. All rights reserved. Trading involves risk.</div>
            <div style={h.footerBadges}>
              {["BLOCKCHAIN","AI","USDT","MLM"].map(b=>(
                <span key={b} style={h.footerBadge}>{b}</span>
              ))}
            </div>
          </div>
        </div>
      </footer>
      </div>
    </div>
  );
}

/* ── Ticker Bar — isolated component, updates DOM directly ── */
function TickerBar() {
  const track1Ref = useRef(null);
  const rafRef    = useRef(null);
  const posRef    = useRef(0);
  const widthRef  = useRef(0);

  // Fetch + update prices directly into DOM — no React state
  useEffect(() => {
    const SYMS = [
      { sym:"BTC",  pair:"BTC/USDT",  b:"BTCUSDT"  },
      { sym:"ETH",  pair:"ETH/USDT",  b:"ETHUSDT"  },
      { sym:"BNB",  pair:"BNB/USDT",  b:"BNBUSDT"  },
      { sym:"SOL",  pair:"SOL/USDT",  b:"SOLUSDT"  },
      { sym:"XRP",  pair:"XRP/USDT",  b:"XRPUSDT"  },
      { sym:"DOGE", pair:"DOGE/USDT", b:"DOGEUSDT" },
      { sym:"ADA",  pair:"ADA/USDT",  b:"ADAUSDT"  },
      { sym:"TRX",  pair:"TRX/USDT",  b:"TRXUSDT"  },
      { sym:"AVAX", pair:"AVAX/USDT", b:"AVAXUSDT" },
      { sym:"DOT",  pair:"DOT/USDT",  b:"DOTUSDT"  },
      { sym:"LINK", pair:"LINK/USDT", b:"LINKUSDT" },
      { sym:"POL",  pair:"POL/USDT",  b:"POLUSDT"  },
    ];

    const updateDOM = (bSym, price, pct) => {
      const col = pct >= 0 ? "#00e87a" : "#ff4d6a";
      const pStr = price > 0
        ? "$" + price.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "—";
      const qStr = `${pct >= 0 ? "▲" : "▼"}${Math.abs(pct).toFixed(2)}%`;
      if (!track1Ref.current) return;
      track1Ref.current.querySelectorAll(`[data-b="${bSym}"] [data-p]`).forEach(el => {
        el.textContent = pStr; el.style.color = col;
      });
      track1Ref.current.querySelectorAll(`[data-b="${bSym}"] [data-q]`).forEach(el => {
        el.textContent = qStr; el.style.color = col;
      });
    };

    // REST fetch first
    const symsStr = SYMS.map(s => `"${s.b}"`).join(",");
    fetch(`https://api.binance.com/api/v3/ticker/24hr?symbols=[${symsStr}]`)
      .then(r => r.json())
      .then(json => {
        if (!Array.isArray(json)) return;
        json.forEach(d => {
          updateDOM(d.symbol, parseFloat(d.lastPrice) || 0, parseFloat(d.priceChangePercent) || 0);
        });
      }).catch(() => {});

    // WebSocket live
    const streams = SYMS.map(s => `${s.b.toLowerCase()}@ticker`).join("/");
    let ws;
    try {
      ws = new WebSocket(`wss://stream.binance.com:9443/stream?streams=${streams}`);
      ws.onmessage = (e) => {
        try {
          const d = JSON.parse(e.data).data;
          if (!d?.s) return;
          updateDOM(d.s, parseFloat(d.c) || 0, parseFloat(d.P) || 0);
        } catch {}
      };
    } catch {}

    return () => { try { ws?.close(); } catch {} };
  }, []);

  // Build static HTML once on mount
  const SYMS_STATIC = [
    { sym:"BTC",pair:"BTC/USDT",b:"BTCUSDT" },{ sym:"ETH",pair:"ETH/USDT",b:"ETHUSDT" },
    { sym:"BNB",pair:"BNB/USDT",b:"BNBUSDT" },{ sym:"SOL",pair:"SOL/USDT",b:"SOLUSDT" },
    { sym:"XRP",pair:"XRP/USDT",b:"XRPUSDT" },{ sym:"DOGE",pair:"DOGE/USDT",b:"DOGEUSDT" },
    { sym:"ADA",pair:"ADA/USDT",b:"ADAUSDT" },{ sym:"TRX",pair:"TRX/USDT",b:"TRXUSDT" },
    { sym:"AVAX",pair:"AVAX/USDT",b:"AVAXUSDT" },{ sym:"DOT",pair:"DOT/USDT",b:"DOTUSDT" },
    { sym:"LINK",pair:"LINK/USDT",b:"LINKUSDT" },{ sym:"POL",pair:"POL/USDT",b:"POLUSDT" },
  ];

  // RAF scroll — single track with doubled items, loop at half point
  useEffect(() => {
    const t1 = track1Ref.current;
    if (!t1) return;
    const raf0 = requestAnimationFrame(() => {
      widthRef.current = t1.scrollWidth / 2; // half = one set of items
      const speed = 0.5;
      const step = () => {
        posRef.current += speed;
        if (posRef.current >= widthRef.current) posRef.current = 0;
        if (track1Ref.current)
          track1Ref.current.style.transform = `translate3d(${-posRef.current}px,0,0)`;
        rafRef.current = requestAnimationFrame(step);
      };
      rafRef.current = requestAnimationFrame(step);
    });
    return () => { cancelAnimationFrame(raf0); cancelAnimationFrame(rafRef.current); };
  }, []);

  // Double items for seamless loop
  const allItems = [...SYMS_STATIC, ...SYMS_STATIC];

  return (
    <div style={h.tickerBar}>
      <div style={{ overflow:"hidden", height:"100%", display:"flex", alignItems:"center" }}>
        <div ref={track1Ref} style={{ display:"flex", alignItems:"center", whiteSpace:"nowrap", willChange:"transform" }}>
          {allItems.map((c, i) => (
            <span key={i} data-b={c.b} style={h.tickerItem}>
              <span style={h.tickerSym}>{c.pair}</span>
              <span data-p style={{ ...h.tickerPrice, color:"#00e87a", display:"inline-block", minWidth:80, textAlign:"right" }}>—</span>
              <span data-q style={{ fontSize:11, color:"#00e87a", fontWeight:700, display:"inline-block", minWidth:56 }}>▲0.00%</span>
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ── Sub-components ────────────────────────────────────── */
function SecHead({ badge, title, sub }) {
  return (
    <div style={{textAlign:"center",marginBottom:52}}>
      <span style={h.secBadge}>{badge}</span>
      <Text3D>{title}</Text3D>
      <p style={h.secSub}>{sub}</p>
      <div style={h.secLine}/>
    </div>
  );
}

function PriceCard({ data }) {
  const dec = data.price>0&&data.price<1?4:2;
  const up  = data.up;
  const pc  = up?"#00e87a":"#ff4d6a";
  return (
    <div style={{...h.priceCard,
      border:`1px solid ${up?"rgba(0,232,122,0.15)":"rgba(255,77,106,0.15)"}`,
      boxShadow:up?"0 0 20px rgba(0,232,122,0.04)":"0 0 20px rgba(255,77,106,0.04)"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:10}}>
        <span style={h.coinSym}>{data.symbol}</span>
        <span style={{...h.pctBadge,color:pc,background:pc+"14"}}>
          {up?"▲":"▼"} {Math.abs(data.pct)}%
        </span>
      </div>
      <div style={{...h.coinPrice,color:pc}}>
        {data.price>0?data.price.toLocaleString("en-US",{minimumFractionDigits:dec}):"—"}
      </div>
      <div style={h.coinUsdt}>USDT</div>
      <MiniChart up={up}/>
    </div>
  );
}

function MiniChart({ up }) {
  const color = up?"#00e87a":"#ff4d6a";
  const [pts, setPts] = useState(()=>genPts(up));
  useEffect(()=>{
    const id=setInterval(()=>{
      setPts(prev=>{
        const arr=prev.split(" ").map(pt=>{
          const [x,y]=pt.split(",").map(Number);
          const ny=Math.max(3,Math.min(26,y+(Math.random()-0.5)*1.8));
          return `${x},${ny.toFixed(1)}`;
        });
        return arr.join(" ");
      });
    },6000);
    return()=>clearInterval(id);
  },[]);
  return (
    <svg width="100%" height="32" viewBox="0 0 110 30" style={{marginTop:8,display:"block"}}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5"
        strokeLinecap="round" strokeLinejoin="round" opacity="0.7"
        style={{transition:"points 2.5s cubic-bezier(0.4,0,0.2,1)"}}/>
    </svg>
  );
}
function genPts(up){
  return Array.from({length:12},(_,i)=>{
    const trend=up?-i*0.9:i*0.9;
    const y=15+trend+(Math.random()-0.5)*5;
    return `${i*10},${Math.max(2,Math.min(28,y)).toFixed(1)}`;
  }).join(" ");
}

/* ── Styles ────────────────────────────────────────────── */
const h = {
  page: { minHeight:"100vh", background:"linear-gradient(160deg, #071428 0%, #050E1E 40%, #081628 70%, #040C1A 100%)", overflowX:"hidden" },
  prelaunchCard: {
    background: "rgba(8,18,40,0.72)",
    backdropFilter: "blur(20px) saturate(160%)",
    WebkitBackdropFilter: "blur(20px) saturate(160%)",
    border: "1px solid rgba(255,184,0,0.18)",
    borderRadius: 16,
    boxSizing: "border-box",
    width: "100%",
    padding: "28px 28px 24px",
    marginBottom: 36,
    boxShadow: "0 4px 16px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset",
  },
  prelaunchTitle: {
    margin: "0 0 10px",
    fontFamily: "'Figtree',sans-serif",
    fontWeight: 700,
    fontSize: "clamp(26px,4vw,36px)",
    background: `linear-gradient(135deg,#f0e0a0,${G},${GD})`,
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    letterSpacing: 1,
  },
  prelaunchLead: {
    margin: "0 auto 18px",
    maxWidth: 640,
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 15,
    lineHeight: 1.7,
    color: "rgba(160,180,220,0.72)",
  },
  prelaunchGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px 18px",
    textAlign: "left",
    marginBottom: 22,
  },
  prelaunchItem: {
    fontFamily: "'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize: 13,
    lineHeight: 1.5,
    color: "rgba(224,232,255,0.82)",
    paddingLeft: 14,
    borderLeft: `2px solid ${G}`,
  },

  /* Ticker */
  tickerBar: { height:46, background:"rgba(3,5,14,0.95)", borderBottom:"1px solid rgba(255,184,0,0.1)", overflow:"hidden", position:"relative", display:"flex", alignItems:"center" },
  tickerTrack: { height:"100%", overflow:"hidden", display:"flex", alignItems:"center" },
  tickerSlide: {
    display:"flex", alignItems:"center", whiteSpace:"nowrap",
    animation:"smoothTicker 45s linear infinite",
    willChange:"transform",
    backfaceVisibility:"hidden",
    WebkitBackfaceVisibility:"hidden",
    transform:"translate3d(0,0,0)",
    WebkitTransform:"translate3d(0,0,0)",
  },
  tickerItem: { display:"inline-flex", alignItems:"center", gap:7, padding:"0 20px", borderRight:"1px solid rgba(255,184,0,0.07)" },
  tickerSym:  { fontFamily:"'Figtree',sans-serif", fontSize:12, color:"rgba(255,184,0,0.6)", letterSpacing:0.5, fontWeight:700 },
  tickerPrice:{ fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:12 },

  /* Hero */
  hero: {
    position:"relative", minHeight:"92vh",
    display:"flex", alignItems:"center",
    justifyContent:"center", overflow:"hidden", padding:"60px 24px",
  },
  bgGrid: {
    position:"absolute",inset:0,
    backgroundImage:`
      linear-gradient(rgba(0,194,255,0.025) 1px, transparent 1px),
      linear-gradient(90deg, rgba(255,184,0,0.018) 1px, transparent 1px)
    `,
    backgroundSize:"60px 60px", pointerEvents:"none",
  },
  glow1: {
    position:"absolute",top:"20%",left:"50%",
    width:900,height:600,
    background:"radial-gradient(ellipse,rgba(255,184,0,0.08) 0%,transparent 70%)",
    pointerEvents:"none",
  },
  glow2: {
    position:"absolute",bottom:"10%",right:"10%",
    width:400,height:400,
    background:"radial-gradient(ellipse,rgba(0,194,255,0.06) 0%,transparent 70%)",
    pointerEvents:"none",
  },
  glow3: {
    position:"absolute",top:"10%",left:"5%",
    width:300,height:300,
    background:"radial-gradient(ellipse,rgba(119,71,255,0.05) 0%,transparent 70%)",
    pointerEvents:"none",
  },
  heroContent: { position:"relative", textAlign:"center", width:"min(860px, 100%)", maxWidth:"100%", zIndex:1 },
  logoAnim: {
    display:"inline-flex", marginBottom:20,
    animation:"floatUp 4s ease-in-out infinite, logoPulse 3s ease-in-out infinite",
  },
  tagline: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:"clamp(14px,2.4vw,21px)",
    background:GRAD, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
    letterSpacing:2, marginBottom:8,
  },
  subTagline: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:500, fontSize:"clamp(12px,1.8vw,15px)",
    color:C2, letterSpacing:1, marginBottom:18,
  },
  pills: { display:"flex", justifyContent:"center", gap:8, flexWrap:"wrap", marginBottom:20 },
  pill: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:700, fontSize:10, color:G,
    background:"rgba(255,184,0,0.07)",
    border:"1px solid rgba(255,184,0,0.25)",
    borderRadius:20, padding:"5px 14px", letterSpacing:1.5,
  },
  liveBadge: {
    display:"inline-flex", alignItems:"center", gap:8,
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:12, fontWeight:600,
    color:"rgba(255,184,0,0.8)",
    background:"rgba(255,184,0,0.06)",
    border:"1px solid rgba(255,184,0,0.18)",
    borderRadius:20, padding:"7px 18px",
    letterSpacing:0.5, marginBottom:22,
  },
  liveDot: {
    width:7,height:7,borderRadius:"50%",
    background:"#00e87a",boxShadow:"0 0 8px #00e87a",
    animation:"pulse-glow 1.5s ease-in-out infinite",display:"block",
  },
  headline: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:900, fontSize:"clamp(42px,8vw,84px)",
    letterSpacing:6, marginBottom:18, lineHeight:1,
  },
  headGold: { background:GRAD, WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent" },
  headWhite:{ color:"rgba(224,232,255,0.9)" },
  heroPara: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:"clamp(13px,2vw,16px)",
    color:"rgba(160,180,220,0.65)",
    lineHeight:1.8, marginBottom:36,
  },
  ctaBtns: { display:"flex", gap:14, justifyContent:"center", flexWrap:"wrap", marginBottom:48 },
  btnPrimary: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:13, color:"#060D1A",
    background:GRAD, border:"none",
    borderRadius:10, padding:"15px 34px",
    textDecoration:"none",
    boxShadow:"0 4px 28px rgba(255,184,0,0.45)",
    letterSpacing:1,
  },
  btnGhost: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:600, fontSize:14,
    color:"rgba(255,184,0,0.85)",
    background:"rgba(255,184,0,0.06)",
    border:"1px solid rgba(255,184,0,0.22)",
    borderRadius:10, padding:"15px 34px",
    textDecoration:"none", letterSpacing:0.5,
  },
  statRow: { display:"flex", gap:10, justifyContent:"center", flexWrap:"wrap" },
  statCard: {
    display:"flex", flexDirection:"column", alignItems:"center",
    background:"rgba(8,18,40,0.75)",
    backdropFilter:"blur(20px) saturate(160%)",
    border:"1px solid", borderRadius:12,
    padding:"12px 20px", minWidth:100,
    boxShadow:"0 4px 16px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset",
  },
  statVal: { fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:18 },
  statLabel: { fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:9, color:"rgba(160,180,220,0.45)", letterSpacing:0.5, textTransform:"uppercase", marginTop:3 },

  /* Sections */
  section: { padding:"80px 24px" },
  container: { maxWidth:1240, margin:"0 auto" },
  secBadge: {
    display:"inline-flex",
    alignItems:"center", justifyContent:"center",
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:700, fontSize:10, color:G,
    background:"rgba(255,184,0,0.08)",
    border:"1px solid rgba(255,184,0,0.2)",
    borderRadius:20, padding:"6px 16px",
    letterSpacing:2, marginBottom:14,
    lineHeight:1,
  },
  secTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:"clamp(22px,4vw,36px)",
    background:`linear-gradient(135deg,#f0e0a0,${G},${GD})`,
    WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
    letterSpacing:2, marginBottom:12,
  },
  secSub: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:15, color:"rgba(160,180,220,0.5)", marginBottom:18,
  },
  secLine: {
    width:80, height:2, margin:"0 auto",
    background:`linear-gradient(90deg,transparent,${G},transparent)`,
  },

  /* Price cards */
  priceGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(175px,1fr))", gap:14 },
  priceCard: {
    background:"rgba(8,18,40,0.75)",
    backdropFilter:"blur(20px) saturate(160%)",
    borderRadius:14, padding:"18px 16px",
    boxShadow:"0 4px 20px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.04) inset",
  },
  coinSym:   { fontFamily:"'Figtree',sans-serif", fontSize:10, color:"rgba(160,180,220,0.5)", letterSpacing:0.5 },
  pctBadge:  { fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontWeight:700, fontSize:10, padding:"3px 7px", borderRadius:5 },
  coinPrice: { fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:18, letterSpacing:0.5 },
  coinUsdt:  { fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:10, color:"rgba(160,180,220,0.35)", letterSpacing:1, marginTop:2 },

  /* Activity */
  actGrid: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:20 },
  feedCard: {
    background:"rgba(8,18,40,0.78)",
    backdropFilter:"blur(24px) saturate(180%)",
    border:"1px solid rgba(0,194,255,0.15)",
    borderRadius:14, padding:"22px", overflow:"hidden",
    boxShadow:"0 8px 32px rgba(0,0,0,0.5), 0 1px 0 rgba(0,194,255,0.08) inset",
  },
  feedHd: { marginBottom:16 },
  liveTag: {
    display:"inline-flex", alignItems:"center", gap:6,
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:700, fontSize:10, color:"#00e87a",
    background:"rgba(0,232,122,0.1)",
    border:"1px solid rgba(0,232,122,0.2)",
    borderRadius:4, padding:"4px 10px", letterSpacing:1.5,
  },
  liveDot2: {
    width:6,height:6,borderRadius:"50%",
    background:"#00e87a",boxShadow:"0 0 6px #00e87a",
    animation:"pulse-glow 1.2s ease-in-out infinite", display:"block",
  },
  feedRow: {
    display:"flex",alignItems:"center",gap:10,
    background:"rgba(0,194,255,0.03)",
    border:"1px solid rgba(0,194,255,0.07)",
    borderRadius:8, padding:"9px 12px", marginBottom:8,
  },
  feedText: { fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:12, flex:1, lineHeight:1.5 },
  feedTime: { fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:10, color:"rgba(160,180,220,0.3)", whiteSpace:"nowrap" },
  bigStats: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 },
  bigStat: {
    background:"rgba(8,18,40,0.78)",
    backdropFilter:"blur(24px) saturate(180%)",
    border:"1px solid",
    borderRadius:12, padding:"22px 18px",
    textAlign:"center",
    display:"flex",flexDirection:"column",alignItems:"center",gap:10,
    boxShadow:"0 4px 20px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.04) inset",
  },
  bigIcon: { width:44,height:44,borderRadius:10,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22 },
  bigVal:  { fontFamily:"'Figtree',sans-serif", fontWeight:700, fontSize:22, letterSpacing:1 },
  bigLabel:{ fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif", fontSize:11, color:"rgba(160,180,220,0.45)", textTransform:"uppercase", letterSpacing:0.5 },

  /* Who we are */
  whoGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:20 },
  glassCard: {
    background:"rgba(8,18,40,0.75)",
    backdropFilter:"blur(24px) saturate(180%)",
    border:"1px solid rgba(255,184,0,0.14)",
    borderRadius:16, padding:"30px 26px",
    position:"relative", overflow:"hidden",
    boxShadow:"0 8px 32px rgba(0,0,0,0.45), 0 2px 0 rgba(255,184,0,0.06) inset, 0 -1px 0 rgba(0,0,0,0.4) inset",
  },
  whoIcon: { fontSize:44, marginBottom:18 },
  whoTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:16, color:G2,
    letterSpacing:1, marginBottom:14,
  },
  whoPara: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:14, color:"rgba(160,180,220,0.65)",
    lineHeight:1.8,
  },

  /* How we work */
  howGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:20, marginBottom:48 },
  howCard: {
    background:"rgba(8,18,40,0.75)",
    backdropFilter:"blur(24px) saturate(180%)",
    border:"1px solid",
    borderRadius:16, padding:"30px 26px",
    position:"relative", overflow:"hidden",
    boxShadow:"0 8px 32px rgba(0,0,0,0.45), 0 1px 0 rgba(255,255,255,0.04) inset",
  },
  howNum: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:900, fontSize:48,
    color:"rgba(255,184,0,0.08)",
    position:"absolute",top:16,right:20,
    lineHeight:1, userSelect:"none",
  },
  howTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:15,
    letterSpacing:1, marginBottom:14,
  },
  howDesc: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:14, color:"rgba(160,180,220,0.65)",
    lineHeight:1.8,
  },
  howAccent: {
    position:"absolute", bottom:0, left:0, right:0, height:2,
    border:"none",
  },

  /* Steps */
  stepsRow: { display:"flex", gap:16, justifyContent:"center", flexWrap:"wrap" },
  stepCard: {
    flex:"0 0 220px",
    background:"rgba(8,18,40,0.8)",
    border:"1px solid rgba(255,184,0,0.1)",
    borderRadius:14, padding:"24px 20px",
    textAlign:"center", position:"relative",
  },
  stepNumCircle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:900, fontSize:13,
    color:"#060D1A",
    background:GRAD,
    borderRadius:"50%",
    width:36,height:36,
    display:"flex",alignItems:"center",justifyContent:"center",
    margin:"0 auto 14px",
    boxShadow:"0 4px 12px rgba(255,184,0,0.4)",
  },
  stepTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:13,
    color:"#e0e8ff", letterSpacing:1, marginBottom:8,
  },
  stepDesc: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:12, color:"rgba(160,180,220,0.55)", lineHeight:1.6,
  },
  stepArrow: {
    position:"absolute",right:-12,top:"50%",
    transform:"translateY(-50%)",
    fontSize:24,color:"rgba(255,184,0,0.25)",zIndex:1,
  },

  /* Vision */
  visionGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(280px,1fr))", gap:20 },
  visionCard: {
    backdropFilter:"blur(20px)",
    border:"1px solid",
    borderRadius:16, padding:"30px 26px",
  },
  visionIcon: { fontSize:44, marginBottom:18 },
  visionTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:16,
    letterSpacing:1, marginBottom:14,
  },
  visionPara: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:14, color:"rgba(160,180,220,0.65)",
    lineHeight:1.8,
  },

  /* Features */
  featGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(240px,1fr))", gap:14 },
  featCard: {
    background:"rgba(8,18,40,0.72)",
    backdropFilter:"blur(20px) saturate(160%)",
    border:"1px solid",
    borderRadius:14, padding:"24px 20px",
    boxShadow:"0 4px 24px rgba(0,0,0,0.4), 0 1px 0 rgba(255,255,255,0.04) inset",
  },
  featIcon: {
    width:44,height:44,borderRadius:10,
    display:"flex",alignItems:"center",justifyContent:"center",
    fontSize:22, marginBottom:14,
  },
  featTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:600, fontSize:12,
    letterSpacing:1, marginBottom:10,
  },
  featDesc: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:13, color:"rgba(160,180,220,0.55)", lineHeight:1.6,
  },

  /* MLM Levels */
  /* Daily Income Plans */
  dailyGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(200px,1fr))", gap:14, marginBottom:24 },
  dailyCard: {
    border:"1px solid", borderRadius:16, padding:"28px 20px",
    textAlign:"center", position:"relative", overflow:"hidden",
    backdropFilter:"blur(16px)", height:"100%",
  },
  dailyTopLine: { position:"absolute", top:0, left:0, right:0, height:3 },
  dailyName: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:13, letterSpacing:1.5,
    textTransform:"uppercase", marginBottom:10,
  },
  dailyRate: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:900, fontSize:34, letterSpacing:1, marginBottom:6,
  },
  dailyRange: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:12, color:"rgba(160,180,220,0.5)", marginBottom:14,
  },
  dailyEg: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:12, fontWeight:600,
    border:"1px solid", borderRadius:8, padding:"8px 12px",
    marginTop:4,
  },
  dailyNote: {
    textAlign:"center",
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:13, color:"rgba(160,180,220,0.45)",
    lineHeight:2, letterSpacing:0.3,
  },

  levelGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(180px,1fr))", gap:12 },
  levelCard: {
    backdropFilter:"blur(14px)",
    border:"1px solid",
    borderRadius:12, padding:"18px 16px",
    textAlign:"center", transition:"transform 0.2s",
  },
  levelLabel: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:11,
    letterSpacing:1.5, marginBottom:6,
  },
  levelPct: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:900, fontSize:28,
    letterSpacing:1, marginBottom:8,
  },
  levelDesc: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:11, color:"rgba(160,180,220,0.45)", lineHeight:1.5,
  },

  /* Contact */
  contactGrid: { display:"grid", gridTemplateColumns:"1fr 1.3fr", gap:24, alignItems:"start" },
  contactInfo: { display:"flex", flexDirection:"column", gap:20 },
  contactItem: {
    display:"flex", alignItems:"flex-start", gap:16,
    background:"rgba(8,18,40,0.7)",
    border:"1px solid rgba(0,194,255,0.1)",
    borderRadius:12, padding:"18px 20px",
  },
  contactIcon: { fontSize:24, flexShrink:0 },
  contactLabel: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:700, fontSize:11,
    color:`rgba(0,194,255,0.6)`,
    letterSpacing:1, textTransform:"uppercase", marginBottom:4,
  },
  contactVal: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:14, color:"#e0e8ff",
  },
  contactCard: {
    background:"rgba(8,18,40,0.85)",
    backdropFilter:"blur(20px)",
    border:"1px solid rgba(255,184,0,0.12)",
    borderRadius:16, overflow:"hidden",
  },
  cardTopLine: {
    height:2,
    background:`linear-gradient(90deg,transparent,${G2},${G},${GD},transparent)`,
  },
  contactForm: { padding:"28px 28px", display:"flex", flexDirection:"column", gap:16 },
  contactFormTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:16,
    color:"#e0e8ff", letterSpacing:1, marginBottom:4,
  },
  fWrap: { display:"flex", flexDirection:"column", gap:6 },
  fLabel: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:600, fontSize:11,
    color:"rgba(255,184,0,0.6)",
    letterSpacing:1.2, textTransform:"uppercase",
  },
  fInput: {
    padding:"12px 14px",
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:14, color:"#e0e8ff",
    background:"rgba(0,0,0,0.45)",
    border:"1px solid rgba(255,184,0,0.12)",
    borderRadius:8, outline:"none",
    transition:"border 0.2s",
    width:"100%",
  },
  contactSubmitBtn: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:700, fontSize:12,
    color:"#060D1A", background:GRAD,
    border:"none", borderRadius:8,
    padding:"13px", cursor:"pointer",
    letterSpacing:1,
    boxShadow:"0 4px 20px rgba(255,184,0,0.35)",
    marginTop:4,
  },
  contactSuccess: {
    padding:"48px 28px",
    textAlign:"center",
  },
  contactResetBtn: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:600, fontSize:13,
    color:G, background:"rgba(255,184,0,0.08)",
    border:"1px solid rgba(255,184,0,0.25)",
    borderRadius:8, padding:"10px 22px",
    cursor:"pointer", marginTop:20,
  },

  /* CTA Section */
  ctaSection: {
    padding:"90px 24px", textAlign:"center",
    position:"relative", overflow:"hidden",
    borderTop:"1px solid rgba(255,184,0,0.1)",
  },
  ctaGlow: {
    position:"absolute",top:"50%",left:"50%",
    transform:"translate(-50%,-50%)",
    width:700,height:400,
    background:"radial-gradient(ellipse,rgba(255,184,0,0.1) 0%,transparent 70%)",
    pointerEvents:"none",
  },
  ctaTitle: {
    fontFamily:"'Figtree',sans-serif",
    fontWeight:900, fontSize:"clamp(24px,5vw,48px)",
    background:`linear-gradient(135deg,#f0e0a0,${G},${GD})`,
    WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent",
    letterSpacing:2, margin:"20px 0 14px",
  },
  ctaSub: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:15, color:"rgba(160,180,220,0.55)",
    lineHeight:1.8, marginBottom:36,
  },

  /* Footer */
  footer: {
    background:"rgba(0,0,0,0.7)",
    backdropFilter:"blur(20px)",
    borderTop:"1px solid rgba(255,184,0,0.08)",
    padding:"40px 24px 24px",
  },
  footerInner: { maxWidth:1240, margin:"0 auto" },
  footerTop: {
    display:"flex", justifyContent:"space-between",
    alignItems:"center", flexWrap:"wrap", gap:20, marginBottom:28,
  },
  footerBrand: { display:"flex", alignItems:"center", gap:12 },
  footerLinks: { display:"flex", gap:24, flexWrap:"wrap" },
  footerLink: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:13, color:"rgba(160,180,220,0.5)",
    textDecoration:"none", letterSpacing:0.3,
    transition:"color 0.2s",
  },
  footerDivider: { height:1, background:"rgba(255,184,0,0.07)", marginBottom:20 },
  footerBottom: {
    display:"flex", justifyContent:"space-between",
    alignItems:"center", flexWrap:"wrap", gap:12,
  },
  footerCopy: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontSize:11, color:"rgba(160,180,220,0.3)",
  },
  footerBadges: { display:"flex", gap:8 },
  footerBadge: {
    fontFamily:"'-apple-system','BlinkMacSystemFont','Figtree',sans-serif",
    fontWeight:700, fontSize:9,
    color:"rgba(255,184,0,0.4)",
    background:"rgba(255,184,0,0.06)",
    border:"1px solid rgba(255,184,0,0.1)",
    borderRadius:4, padding:"3px 8px", letterSpacing:1.5,
  },
};




